<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useReserveStore } from '@/stores/reserve/reserveStore';
    import { useLanguageStore } from '@/stores/common/languageStore';

    // Vue
    import { onMounted, reactive } from 'vue';
    import { useRouter } from 'vue-router';
    import { getCurrentInstance } from 'vue';
    import { inject } from 'vue';
    import { ref } from 'vue';

    // Utils
    import utils from '@/commonProject/helpers/utils';

    // ----------------------------
    // 定数
    // ----------------------------
    const router = useRouter();
    const commonStore = useCommonStore();
    const reserveStore = useReserveStore();
    const languageStore = useLanguageStore();
    const app = getCurrentInstance();

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // ----------------------------
    // リアクティブ変数
    // ----------------------------
    const requestData = reactive(reserveStore.rsvdtlRequest);

    const selectedProgramInfo = ref(null);

    // ----------------------------
    // 関数
    // ----------------------------
    // 戻るボタン押下時
    function onBack() {
        // リクエスト情報クリア
        //reserveStore.clearRequestData();
        reserveStore.clearAll();

        // 遷移元画面をリセットする。
        commonStore.reset();

        // 遷移元の画面に戻る。遷移元はホーム画面。
        router.push({ name: 'home' });
    }

    // 進むボタン押下時
    async function onAdvance() {
        // 遷移元画面を「予約(割り当てからサービス選択)画面」に設定する。
        commonStore.setFrom('personal');

        try {
            // 選択したプログラムのサービスPKとセッショングループPKを保持する。
            const [ServicePk, SessionGroupPk] = selectedProgramInfo.value.split('_____');
            requestData.ServicePk = ServicePk;
            requestData.SessionGrpPk = SessionGroupPk;
        } catch (e) {
            return;
        }

        // サービスPKの保持に失敗した場合は、以降の処理を行わない。
        if (utils.isNullEmpty(requestData.ServicePk)) {
            return;
        }

        // サービス詳細取得APIのクエリパラメーターを生成する。
        const queryParam = {
            Lang: languageStore.getLanguageValue,
            ServicePk: requestData.ServicePk,
        };

        // サービス詳細取得APIをコールする。
        const response = await reserveStore.getServiceDetailAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, queryParam);

        if (response.success) {
            requestData.RegulationPk = reserveStore.serviceDetail.RegulationPk;

            // セッショングループ詳細取得APIのクエリパラメーターを生成する。
            const queryParam2 = {
                //SessionGrpPk: reserveStore.reservePlanningPersonalSelectedSessionGroupPk,
                SessionGrpPk: requestData.SessionGrpPk,
            };

            // セッショングループ詳細取得APIをコールする。
            const response2 = await reserveStore.getSessionGroupDetailAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, queryParam2);
            if (response2.success) {
                // データを保管する。
                reserveStore.setRsvdtlRequestData(requestData);

                // セッショングループのセッション件数を判定する。
                if (reserveStore.sessionNum === 0) {
                    // セッション件数が0件の場合は、まだセッションを未受講なので、同意画面に遷移する。

                    // 予約(規定同意)画面へ遷移する。
                    router.push({ name: 'reserveplanningagree' });
                } else {
                    // セッション件数が存在する場合は、一度でもセッションを受講したことがあるので、同意画面に遷移せずに、選択画面に遷移する。

                    // 予約(プログラムから利用方法など選択)画面へ遷移する。
                    router.push({ name: 'reserveplanningselection' });
                }

                /*
                if (utils.isNullEmpty(requestData.RegulationPk)) {
                    // 規定PKがない場合
                    // 予約(プログラムから利用方法など選択)画面へ遷移する。
                    //router.push({ name: 'reserveplanningselection' });
                } else {
                    // 規定PKがある場合
                    // 予約(規定同意)画面へ遷移する。
                    //router.push({ name: 'reserveplanningagree' });
                }
                */
            } else {
                messageDialog('error', response2.errorMessage);
                if (response2.redirect) {
                    location.href = response2.redirectUrl;
                }
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    // ----------------------------
    // ライフサイクル
    // ----------------------------
    onMounted(async () => {
        // 前画面から戻ってきたか判定する。
        if (!commonStore.reservePageBack) {
            // 遷移元画面をリセットする。
            commonStore.reset();

            // 初期化
            reserveStore.clearAll();
        } else {
            // 前画面から戻ってきた場合、座標記録をクリアする。
            reserveStore.clearReserveStep();
        }

        // 前画面からの戻るフラグを初期化する。
        commonStore.reservePageBack = false;

        // 戻り先座標を記録する。
        // 遷移元予約画面のルートと、設定・選択状況に応じて、戻る画面を制御する。
        // 割当てられたプログラムを予約のルート
        reserveStore.reserveStep.personal.reserveplanningpersonal = true;

        // サービス一覧取得APIのリクエストボディを作成する。
        const serviceRequest = {
            Lang: languageStore.getLanguageValue,
            SelfUseMethod: '0',
            AssignedProgram: true,
            Used3MonthAgo: false,
        };

        // サービス一覧取得APIをコールする。
        const response = await reserveStore.getReserveServiceAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, serviceRequest);

        if (response.success) {
            // APIから取得したサービスの一覧を画面に描画する。
            requestData.serviceList = reserveStore.serviceList;

            reserveStore.setRsvdtlRequestData(requestData);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }

        // 前画面から戻ってきた場合、選択したプログラムを復元する。
        if (!utils.isNullEmpty(requestData.SessionGrpPk)) {
            // 本画面は、サービスPKと、セッショングループPKを組み合わせる。
            selectedProgramInfo.value = requestData.ServicePk + '_____' + requestData.SessionGrpPk;
        }

        // サービスが1件しかない場合
        //if (requestData.serviceList.length === 1) {
        /*
        requestData.ServicePk = reserveStore.serviceList[0].ServicePk;
        const queryParam = {
            Lang: languageStore.getLanguageValue,
            ServicePk: requestData.ServicePk,
        };
        // サービス詳細取得API
        await reserveStore.getServiceDetailAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, queryParam);
        requestData.RegulationPk = reserveStore.serviceDetail.RegulationPk;
        if (utils.isNullEmpty(requestData.RegulationPk)) {
            // 規定PKがない場合
            commonStore.setFrom('personal');
            router.push({ name: 'reserveplanningselection' });
        } else {
            // 規定PKがある場合
            commonStore.setFrom('personal');
            router.push({ name: 'reserveplanningagree' });
        }
        */

        //}
    });
</script>

<template>
    <section id="main">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3 class="reserve">
                    <span>{{ $t('reservePlanningPersonal.parts.pageTitle1') }}</span>
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <img src="/img/step/mode3_step1.png" class="step" width="410" height="46" />
                    </template>
                    <template v-else>
                        <img src="/img/step/mode3_step1_en.png" class="step" width="410" height="46" />
                    </template>
                </h3>
                <section class="whiteBoxContents">
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <img src="/img/step/mode3_step1.png" class="step" width="410" height="46" />
                    </template>
                    <template v-else>
                        <img src="/img/step/mode3_step1_en.png" class="step" width="410" height="46" />
                    </template>
                    <p class="pageInfo">{{ $t('reservePlanningPersonal.parts.pageInformation1') }}</p>
                    <ul class="serviceList">
                        <li v-for="service in requestData.serviceList" :key="service.ServiceId">
                            <input type="radio" :id="'radio' + service.ServicePk + '_' + service.SessionGrpPk" name="selectedServiceList" :value="service.ServicePk + '_____' + service.SessionGrpPk" v-model="selectedProgramInfo" />
                            <label :for="'radio' + service.ServicePk + '_' + service.SessionGrpPk">
                                <ul class="serviceData">
                                    <li>
                                        <span>{{ service.ServiceNm }}</span>
                                    </li>
                                    <li>
                                        <span>{{ service.ServiceExp }}</span>
                                    </li>
                                </ul>
                            </label>
                        </li>
                    </ul>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <!--戻る-->
                            <button type="button" class="btnNormal" @click="onBack">{{ $t('common.parts.backPage') }}</button>
                        </li>
                        <li>
                            <!--進む-->
                            <button type="button" class="btnPrimary" @click="onAdvance" :disabled="utils.isNullEmpty(selectedProgramInfo)">{{ $t('common.parts.nextPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style></style>
